<template>
  <div class="space-y-4">
    <!-- Summary -->
    <div class="mt-2 space-y-2">
      <div class="flex justify-between">
        <span class="text-gray-600 dark:text-gray-400">
          {{ t('app.views.cashRegister.totalSales') || 'Total Sales' }}
        </span>
        <span class="font-medium">{{ formatCurrency(report.total_sales || 0) }}</span>
      </div>
      <div class="flex justify-between">
        <span class="text-gray-600 dark:text-gray-400">
          {{ t('app.views.cashRegister.totalExpenses') || 'Total Expenses' }}
        </span>
        <span class="font-medium text-red-600 dark:text-red-400">{{ formatCurrency(report.total_expenses || 0)
        }}</span>
      </div>
      <div class="flex justify-between ">
        <span class="text-gray-900 dark:text-gray-100">
          {{ t('app.views.cashRegister.netCashFlow') || 'Net Cash Flow' }}
        </span>
        <span>{{ formatCurrency(report.net_cash_flow || 0) }}</span>
      </div>
    </div>

    <!-- Payment Breakdown -->
    <div>
      <div class="mt-2 grid grid-cols-2 gap-3">
        <div
          v-for="method in allPaymentMethods"
          :key="method.key"
        >
          <label
            :class="getPaymentMethodLabelClass(method.key)"
            class="block text-sm font-medium mb-1"
          >
            {{ method.label }}
          </label>
          <div
            :class="getPaymentMethodDisplayClass(method.key)"
            class="w-full rounded-md px-3 py-2 text-sm font-medium"
          >
            {{ formatCurrency(paymentValues[method.key] || 0) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Cash Flow Details -->
    <div v-if="props.initialBalance !== undefined" class="mt-6">
      <CashFlowDetails
        :initial-balance="props.initialBalance"
        :cash-sales="props.cash"
        :expenses="props.report.total_expenses"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';
import CashFlowDetails from './CashFlowDetails.vue';

const { t } = useI18n();
const authStore = useAuthStore();

const props = defineProps<{
  report: {
    total_sales: number;
    total_expenses: number;
    net_cash_flow: number;
    payment_breakdown: Record<string, number>;
  };
  cash: number;
  card: number;
  digital: number;
  other: number;
  customPayments: Record<string, number>;
  initialBalance?: number;
}>();

// Create ref for all payment method values
const paymentValues = ref<Record<string, number>>({
  cash: props.cash,
  card: props.card,
  digital: props.digital,
  other: props.other
});

// Initialize custom payment methods
const initializeCustomMethods = () => {
  const customMethods = authStore.restaurant?.payment_methods_config?.custom_methods || [];

  // Start with standard methods from props
  const currentValues = {
    cash: props.cash,
    card: props.card,
    digital: props.digital,
    other: props.other
  };

  // Add custom methods from props.customPayments
  customMethods.forEach((method: any) => {
    if (method.enabled) {
      // Try case-insensitive matching for the payment method name
      const value = props.customPayments[method.name] ||
        props.customPayments[method.name.toLowerCase()] ||
        props.customPayments[method.name.toUpperCase()] || 0;
      currentValues[method.name] = value;
    }
  });

  paymentValues.value = currentValues;
};

// Initialize on mount and watch for restaurant changes
initializeCustomMethods();
watch(() => authStore.restaurant?.payment_methods_config?.custom_methods, initializeCustomMethods, { deep: true });
watch(() => props.customPayments, initializeCustomMethods, { deep: true, immediate: true });

// Watch for prop changes
watch(() => props.cash, (newVal) => {
  paymentValues.value = { ...paymentValues.value, cash: newVal };
});
watch(() => props.card, (newVal) => {
  paymentValues.value = { ...paymentValues.value, card: newVal };
});
watch(() => props.digital, (newVal) => {
  paymentValues.value = { ...paymentValues.value, digital: newVal };
});
watch(() => props.other, (newVal) => {
  paymentValues.value = { ...paymentValues.value, other: newVal };
});

// Get all payment methods including custom ones
const allPaymentMethods = computed(() => {
  const methods = [
    { key: 'cash', label: t('app.views.cashRegister.cashPayments') || 'Cash Payments' },
    { key: 'card', label: t('app.views.cashRegister.cardPayments') || 'Card Payments' },
    { key: 'digital', label: t('app.views.cashRegister.digitalPayments') || 'Digital Payments' },
    { key: 'other', label: t('app.views.cashRegister.otherPayments') || 'Other Payments' }
  ];

  // Add custom methods
  const customMethods = authStore.restaurant?.payment_methods_config?.custom_methods || [];
  customMethods.forEach((method: any) => {
    if (method.enabled) {
      methods.push({
        key: method.name,
        label: `${method.name}`
      });
    }
  });

  return methods;
});

// Helper functions for payment method colors
const getPaymentMethodLabelClass = (methodKey: string): string => {
  const method = methodKey?.toUpperCase();
  switch (method) {
    case 'CASH':
      return 'text-green-700 dark:text-green-400';
    case 'CARD':
      return 'text-blue-700 dark:text-blue-400';
    case 'DIGITAL':
      return 'text-purple-700 dark:text-purple-400';
    case 'OTHER':
      return 'text-gray-700 dark:text-gray-300';
    default:
      // Custom payment methods get unique colors
      return getCustomPaymentMethodLabelColor(methodKey);
  }
};

// Generate unique colors for custom payment method labels
const getCustomPaymentMethodLabelColor = (methodKey: string): string => {
  const colors = [
    'text-orange-700 dark:text-orange-400',
    'text-pink-700 dark:text-pink-400',
    'text-indigo-700 dark:text-indigo-400',
    'text-teal-700 dark:text-teal-400',
    'text-amber-700 dark:text-amber-400'
  ];

  // Generate consistent color based on method name hash (same as input colors)
  let hash = 0;
  for (let i = 0; i < methodKey.length; i++) {
    hash = methodKey.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colorIndex = Math.abs(hash) % colors.length;

  return colors[colorIndex];
};

const getPaymentMethodDisplayClass = (methodKey: string): string => {
  const method = methodKey?.toUpperCase();
  switch (method) {
    case 'CASH':
      return 'border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-900 dark:text-green-100';
    case 'CARD':
      return 'border border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-900 dark:text-blue-100';
    case 'DIGITAL':
      return 'border border-purple-300 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 text-purple-900 dark:text-purple-100';
    case 'OTHER':
      return 'border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100';
    default:
      // Custom payment methods get unique colors based on hash of method name
      return getCustomPaymentMethodDisplayColor(methodKey);
  }
};

// Generate unique colors for custom payment methods
const getCustomPaymentMethodDisplayColor = (methodKey: string): string => {
  const colors = [
    {
      border: 'border-orange-300 dark:border-orange-800',
      bg: 'bg-orange-50 dark:bg-orange-900/20',
      text: 'text-orange-900 dark:text-orange-100'
    },
    {
      border: 'border-pink-300 dark:border-pink-800',
      bg: 'bg-pink-50 dark:bg-pink-900/20',
      text: 'text-pink-900 dark:text-pink-100'
    },
    {
      border: 'border-indigo-300 dark:border-indigo-800',
      bg: 'bg-indigo-50 dark:bg-indigo-900/20',
      text: 'text-indigo-900 dark:text-indigo-100'
    },
    {
      border: 'border-teal-300 dark:border-teal-800',
      bg: 'bg-teal-50 dark:bg-teal-900/20',
      text: 'text-teal-900 dark:text-teal-100'
    },
    {
      border: 'border-amber-300 dark:border-amber-800',
      bg: 'bg-amber-50 dark:bg-amber-900/20',
      text: 'text-amber-900 dark:text-amber-100'
    }
  ];

  // Generate consistent color based on method name hash
  let hash = 0;
  for (let i = 0; i < methodKey.length; i++) {
    hash = methodKey.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colorIndex = Math.abs(hash) % colors.length;
  const color = colors[colorIndex];

  return `border ${color.border} ${color.bg} ${color.text}`;
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN'
  }).format(value || 0);
};
</script>
